// ---------------------------------------------------------------------------
// Field-spec read-back — the deterministic half of S2 in
// docs/studio/wonbanking-studio-scenarios-2026-08-08.md.
//
// A bank developer often arrives with only the message spec's field table:
// pasted out of Excel (tab-separated) or as a markdown table, headers in
// Korean, sometimes split into 입력부/출력부 blocks. The list, types, lengths
// and required flags are decidable from the paste; which fields appear ON
// SCREEN versus travel internally is not — the scenarios doc names that
// single question as this scenario's whole bottleneck, and it belongs to the
// human. So this module parses without a model in the loop, and
// `formatFieldVisibilityDecision` serializes the human's checkbox pass into a
// stable block the generation prompt can carry.
//
// Conservative on purpose: text that does not look like a field table — no
// tab/pipe structure, or no recognizable name-column header — returns null
// and the creation router treats it exactly as before. JSON is refused here
// even though the router checks the S1 contract lane first, so this parser
// stays safe under reordering.
// ---------------------------------------------------------------------------

export type FieldSpecSection = 'input' | 'output' | 'common' | 'unknown'

export interface FieldSpecField {
  name: string
  type: string | null
  length: string | null
  /** null = the table did not say; the card renders nothing rather than a guess. */
  required: boolean | null
  section: FieldSpecSection
  description: string | null
}

export interface FieldSpecUnread {
  /**
   * Header cells that matched none of the six column kinds this parser knows.
   * NOTHING on the card reflects them — not the type line, not the required
   * mark, not the description. A `필수구분` column of all-`Y` and a `코드값`
   * column of code values are both invisible here, and invisible is exactly
   * what "the table said nothing" looks like, so the names travel out and the
   * card says them.
   */
  columns: string[]
  /**
   * Data rows dropped because their name cell was empty — the shape an Excel
   * MERGED cell produces on its continuation rows. The card's field count is
   * a count of what was read; this is the rest of the table.
   */
  skippedRows: number
  /**
   * Values held by a RECOGNIZED required column that `readRequired` could not
   * interpret — `예`/`아니오`, `*`, a footnote marker.
   *
   * This is the hole the required-header widening opened. Before it, a
   * `필수구분` column matched no known kind and `columns` above named it; now
   * the column is read, its values are not, and `required: null` renders as
   * blank — pixel-identical to a table with no required column at all. An
   * EMPTY cell is not here: "the table did not say" is a true reading, and
   * `required: null` states it exactly.
   */
  requiredValues: string[]
}

export interface FieldSpecReadback {
  source: 'field-table'
  delimiter: 'tsv' | 'markdown'
  fields: FieldSpecField[]
  sectionCounts: Record<FieldSpecSection, number>
  /** What was in the paste and is not on the card. Never silently dropped. */
  unread: FieldSpecUnread
}

const NAME_HEADERS = ['필드명', '항목명', '영문명', '필드', '항목', 'name', 'field']
const TYPE_HEADERS = ['타입', '유형', '데이터타입', 'type']
const LENGTH_HEADERS = ['길이', '자릿수', 'length', 'len', 'size']
const REQUIRED_HEADERS = ['필수여부', '필수', 'required', 'm/o', 'mo']
const SECTION_HEADERS = ['구분', '입출력', '방향', 'in/out', 'i/o', 'io']
const DESCRIPTION_HEADERS = ['설명', '비고', '내용', 'description', 'desc']

function headerIndex(cells: string[], candidates: string[]): number {
  const lowered = cells.map((cell) => cell.trim().toLowerCase())
  for (const candidate of candidates) {
    const index = lowered.indexOf(candidate)
    if (index !== -1) return index
  }
  return -1
}

/**
 * The required column, by exact header OR by the Korean stem 필수.
 *
 * A 전문 spec writes this column 필수여부, 필수구분, 필수Y/N — all the same
 * column, and 필수 leading a field-table header means requiredness in this
 * corpus; there is no other reading. An exact list of synonyms leaves the
 * next spelling unread, which the sweep measured on 필수구분: every row said
 * `Y` and the card marked nothing required. Deliberately narrow — a header
 * that merely CONTAINS 필수 elsewhere is not claimed, and this latitude is
 * not extended to any other column kind.
 */
function requiredHeaderIndex(cells: string[]): number {
  const exact = headerIndex(cells, REQUIRED_HEADERS)
  if (exact !== -1) return exact
  return cells.findIndex((cell) => cell.trim().startsWith('필수'))
}

function readSection(value: string): FieldSpecSection | null {
  const v = value.trim().toLowerCase()
  if (/^(입력|input|in|req|i)(부)?$/.test(v)) return 'input'
  if (/^(출력|output|out|res|o)(부)?$/.test(v)) return 'output'
  if (/^(공통|common)(부)?$/.test(v)) return 'common'
  return null
}

/** A standalone line like "[입력부]" / "출력부" between table blocks. */
function readSectionHeaderLine(line: string): FieldSpecSection | null {
  const bare = line.trim().replace(/^[[<(【]|[\]>)】]$/g, '')
  return readSection(bare)
}

function readRequired(value: string): boolean | null {
  const v = value.trim().toLowerCase()
  if (v === '') return null
  // M/O is mandatory/optional; ●/○ is the filled/empty-circle convention.
  if (['y', 'm', '필수', 'true', '●', 'o(필수)'].includes(v)) return true
  if (['n', 'o', '선택', 'false', '○', '-'].includes(v)) return false
  return null
}

function cellOrNull(cells: string[], index: number): string | null {
  if (index === -1) return null
  const value = cells[index]?.trim()
  return value ? value : null
}

interface Line {
  kind: 'row' | 'section'
  cells?: string[]
  section?: FieldSpecSection
}

function splitTsv(text: string): Line[] | null {
  const raw = text.split(/\r?\n/).map((line) => line.trimEnd())
  const lines: Line[] = []
  let tabbed = 0
  for (const line of raw) {
    if (!line.trim()) continue
    if (line.includes('\t')) {
      tabbed += 1
      lines.push({ kind: 'row', cells: line.split('\t').map((c) => c.trim()) })
      continue
    }
    const section = readSectionHeaderLine(line)
    if (section) {
      lines.push({ kind: 'section', section })
      continue
    }
    // A non-tab, non-section line means this is prose with a stray tab
    // somewhere, not a table paste. Refuse the whole parse.
    return null
  }
  return tabbed >= 2 ? lines : null
}

function splitMarkdown(text: string): Line[] | null {
  const raw = text.split(/\r?\n/).map((line) => line.trim())
  const lines: Line[] = []
  let piped = 0
  for (const line of raw) {
    if (!line) continue
    if (line.startsWith('|')) {
      const cells = line
        .replace(/^\|/, '')
        .replace(/\|$/, '')
        .split('|')
        .map((c) => c.trim())
      // Alignment separator (| --- | --- |) carries no data.
      if (cells.every((cell) => /^:?-{2,}:?$/.test(cell) || cell === '')) continue
      piped += 1
      lines.push({ kind: 'row', cells })
      continue
    }
    const section = readSectionHeaderLine(line)
    if (section) {
      lines.push({ kind: 'section', section })
      continue
    }
    return null
  }
  return piped >= 2 ? lines : null
}

export function parseFieldSpecReadback(text: string): FieldSpecReadback | null {
  const trimmed = text.trim()
  // JSON belongs to the S1 contract lane; refuse it here so this parser is
  // order-independent of the router.
  if (trimmed.startsWith('{') || trimmed.startsWith('```')) return null

  let delimiter: 'tsv' | 'markdown'
  let lines = splitTsv(trimmed)
  if (lines) {
    delimiter = 'tsv'
  } else {
    lines = splitMarkdown(trimmed)
    if (!lines) return null
    delimiter = 'markdown'
  }

  const fields: FieldSpecField[] = []
  const unreadColumns: string[] = []
  const unreadRequiredValues: string[] = []
  let skippedRows = 0
  let currentSection: FieldSpecSection = 'unknown'
  let columns: {
    name: number
    type: number
    length: number
    required: number
    section: number
    description: number
  } | null = null

  for (const line of lines) {
    if (line.kind === 'section') {
      currentSection = line.section!
      continue
    }
    const cells = line.cells!
    const nameHeader = headerIndex(cells, NAME_HEADERS)
    if (nameHeader !== -1) {
      // A header row — either the table's first, or repeated per section
      // block. Re-reading it keeps per-block column orders independent.
      columns = {
        name: nameHeader,
        type: headerIndex(cells, TYPE_HEADERS),
        length: headerIndex(cells, LENGTH_HEADERS),
        required: requiredHeaderIndex(cells),
        section: headerIndex(cells, SECTION_HEADERS),
        description: headerIndex(cells, DESCRIPTION_HEADERS),
      }
      // Every other cell of this header row names a column that will not
      // appear anywhere on the card. Recorded here, at the only place the
      // column vocabulary is decided, so a new column kind cannot be added
      // without this list shrinking in the same edit.
      const mapped = new Set(Object.values(columns))
      cells.forEach((cell, index) => {
        const header = cell.trim()
        if (!header || mapped.has(index)) return
        if (!unreadColumns.includes(header)) unreadColumns.push(header)
      })
      continue
    }
    if (!columns) {
      // Data before any recognizable header: not a field table we can read
      // truthfully. Refuse rather than guess column meanings.
      return null
    }
    const name = cellOrNull(cells, columns.name)
    if (!name) {
      // An Excel merged cell's continuation row. Counted, not dismissed: the
      // card's "N fields" would otherwise report the smaller table as THE
      // table, and nothing on screen would differ from a table of N rows.
      if (cells.some((cell) => cell.trim())) skippedRows += 1
      continue
    }
    const sectionCell = cellOrNull(cells, columns.section)
    const section = (sectionCell && readSection(sectionCell)) || currentSection
    const requiredCell = cellOrNull(cells, columns.required)
    const required = readRequired(requiredCell ?? '')
    // The column was recognized and this cell HELD something the reader could
    // not interpret. Silence here renders as an unmarked field, which is what
    // "the table did not say" also renders as.
    if (
      requiredCell !== null &&
      required === null &&
      !unreadRequiredValues.includes(requiredCell)
    ) {
      unreadRequiredValues.push(requiredCell)
    }
    fields.push({
      name,
      type: cellOrNull(cells, columns.type),
      length: cellOrNull(cells, columns.length),
      required,
      section,
      description: cellOrNull(cells, columns.description),
    })
  }

  // One row is a table. The paste already had to carry two delimiter lines
  // AND a recognized name-column header to get here, so a single-row spec is
  // not ambiguous with prose — and the old `< 2` floor made the surface
  // silently stop reading back below it, which is the one thing this card
  // must never do.
  if (fields.length < 1) return null

  const sectionCounts: Record<FieldSpecSection, number> = {
    input: 0,
    output: 0,
    common: 0,
    unknown: 0,
  }
  for (const field of fields) sectionCounts[field.section] += 1

  return {
    source: 'field-table',
    delimiter,
    fields,
    sectionCounts,
    unread: {
      columns: unreadColumns,
      skippedRows,
      requiredValues: unreadRequiredValues,
    },
  }
}

/**
 * Serializes the human's checkbox pass. Stable, language-independent keys so
 * the generation prompt carries the decision verbatim; grouped by section
 * because "query condition or result?" is the other question S2 says the
 * screen must keep distinct. An all-visible pass is still emitted — the
 * point is that the human decided, and the record says so.
 *
 * A name declared in TWO sections (a 전문's request `custNo` and its response
 * echo — the normal case, not a corner case) is written `section.name` on
 * both sides. With bare names the human's actual ruling ("show the request
 * one, hide the echo") serialized as `visible(input): custNo` AND
 * `internal: custNo`, which the planner section then flattened into "show
 * custNo" beside "never render custNo" — a contradiction, persisted to the
 * project as the human's confirmed decision. Names that occur once stay bare,
 * so the ordinary spec's decision block is unchanged.
 */
export function formatFieldVisibilityDecision(
  readback: FieldSpecReadback,
  visibleByIndex: readonly boolean[],
): string {
  const sectionsByName = new Map<string, Set<FieldSpecSection>>()
  for (const field of readback.fields) {
    const sections = sectionsByName.get(field.name) ?? new Set()
    sections.add(field.section)
    sectionsByName.set(field.name, sections)
  }
  const nameOf = (field: FieldSpecField): string =>
    (sectionsByName.get(field.name)?.size ?? 0) > 1
      ? `${field.section}.${field.name}`
      : field.name

  const bySection: Record<FieldSpecSection, string[]> = {
    input: [],
    output: [],
    common: [],
    unknown: [],
  }
  const internal: string[] = []
  readback.fields.forEach((field, index) => {
    if (visibleByIndex[index] === false) internal.push(nameOf(field))
    else bySection[field.section].push(nameOf(field))
  })

  const lines = ['[field-visibility-decision]']
  for (const section of ['input', 'output', 'common', 'unknown'] as const) {
    if (bySection[section].length > 0) {
      lines.push(`visible(${section}): ${bySection[section].join(', ')}`)
    }
  }
  if (internal.length > 0) lines.push(`internal: ${internal.join(', ')}`)
  return lines.join('\n')
}
